import React, { useEffect, useMemo, useState } from "react";

import { getSettings } from "../../../../../services/settingsServices";
import { getAllAdherentsListWithGroup } from "../../../../../services/adherentServices";

import {
  FaCheckCircle,
  FaClock,
  FaHourglassHalf,
  FaPauseCircle,
  FaRedo,
  FaUserSlash,
  FaUsers,
} from "react-icons/fa";

const STATUS_META = [
  {
    key: "Licencié",
    label: "Licenciés",
    shortLabel: "Licenciés",
    icon: <FaCheckCircle />,
    dot: "bg-emerald-500",
    accent:
      "from-emerald-600/15 to-lime-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200/70 dark:ring-emerald-500/20",
  },
  {
    key: "Licencié Hors Club",
    label: "Licenciés Hors Club",
    shortLabel: "Hors club",
    icon: <FaUsers />,
    dot: "bg-teal-400",
    accent:
      "from-teal-600/15 to-cyan-500/10 text-teal-700 dark:text-teal-300 ring-teal-200/70 dark:ring-teal-500/20",
  },
  {
    key: "Inscrit",
    label: "Inscrits",
    shortLabel: "Inscrits",
    icon: <FaClock />,
    dot: "bg-amber-500",
    accent:
      "from-amber-500/15 to-orange-500/10 text-amber-700 dark:text-amber-300 ring-amber-200/70 dark:ring-amber-500/20",
  },
  {
    key: "Cours d'essai",
    label: "Cours d’essai",
    shortLabel: "Essais",
    icon: <FaHourglassHalf />,
    dot: "bg-rose-500",
    accent:
      "from-rose-600/15 to-pink-500/10 text-rose-700 dark:text-rose-300 ring-rose-200/70 dark:ring-rose-500/20",
  },
  {
    key: "Licencié - Arrêt",
    label: "Licenciés - Arrêt",
    shortLabel: "Arrêts",
    icon: <FaUserSlash />,
    dot: "bg-slate-500",
    accent:
      "from-slate-600/15 to-slate-400/10 text-slate-700 dark:text-slate-300 ring-slate-200/70 dark:ring-slate-500/20",
  },
];

const EMPTY_COUNTS = {
  Licencié: 0,
  "Licencié Hors Club": 0,
  Inscrit: 0,
  "Cours d'essai": 0,
  "Licencié - Arrêt": 0,
};

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.adherents)) return data.adherents;
    if (Array.isArray(data.data)) return data.data;
  }

  return [];
}

function unwrapSettings(settings) {
  if (!settings) return {};

  let output = settings;

  if (Array.isArray(output)) output = output[0] || {};
  if (output?.dataValues) output = output.dataValues;

  return output || {};
}

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .replace("Cours d’essai", "Cours d'essai");
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function getPercent(value, total) {
  if (!total) return 0;
  return Math.round((Number(value || 0) / total) * 100);
}

function StatusMiniCard({ meta, value, total }) {
  const percent = getPercent(value, total);

  return (
    <div
      className={`rounded-3xl bg-gradient-to-br ${meta.accent} p-4 shadow-sm ring-1`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-base dark:bg-slate-900/50">
            {meta.icon}
          </div>

          <div className="min-w-0">
            <p className="truncate text-[11px] font-bold uppercase tracking-[0.16em] opacity-70">
              {meta.shortLabel}
            </p>

            <p className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
              {formatNumber(value)}
            </p>
          </div>
        </div>

        <span className="rounded-full bg-white/70 px-2 py-1 text-xs font-black text-slate-700 ring-1 ring-white/80 dark:bg-slate-950/40 dark:text-slate-200 dark:ring-slate-700/70">
          {percent}%
        </span>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="h-4 w-48 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
            <div className="mt-2 h-3 w-64 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
          </div>

          <div className="h-12 w-24 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="h-24 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-black">Erreur de chargement</p>

      <p className="mt-1 text-sm leading-6">{message}</p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-black text-white transition hover:bg-rose-700"
      >
        <FaRedo />
        Réessayer
      </button>
    </div>
  );
}

export default function AdherentStatusBreakdownCard() {
  const [season, setSeason] = useState("");
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const settingsResponse = await getSettings();
      const settings = unwrapSettings(settingsResponse);
      const activeSeason = settings?.saison_active || "";

      setSeason(activeSeason);

      const adherentsResponse = await getAllAdherentsListWithGroup();
      const adherents = extractArray(adherentsResponse);

      const filtered = adherents.filter((adherent) => {
        return String(adherent?.saison_active || "") === String(activeSeason);
      });

      const nextCounts = { ...EMPTY_COUNTS };

      for (const adherent of filtered) {
        const status = normalizeStatus(adherent?.statut_inscription);

        if (status in nextCounts) {
          nextCounts[status] += 1;
        }
      }

      setCounts(nextCounts);
    } catch (error) {
      console.error("Erreur chargement détail adhérents :", error);
      setCounts(EMPTY_COUNTS);
      setErrorMsg(
        "Impossible de récupérer le détail des adhérents par statut."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const total = useMemo(() => {
    return STATUS_META.reduce((sum, meta) => sum + Number(counts[meta.key] || 0), 0);
  }, [counts]);

  const mainStatus = useMemo(() => {
    return STATUS_META.reduce(
      (best, meta) => {
        const value = Number(counts[meta.key] || 0);
        return value > best.value ? { ...meta, value } : best;
      },
      { label: "—", value: 0 }
    );
  }, [counts]);

  if (loading) return <LoadingSkeleton />;

  if (errorMsg) {
    return <ErrorPanel message={errorMsg} onRetry={fetchData} />;
  }

  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Saison {season || "—"}
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Détail des adhérents par statut
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Répartition des dossiers d’inscription sur la saison active.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[360px]">
            <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] opacity-75">
                Total saison
              </p>
              <p className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
                {formatNumber(total)}
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] opacity-75">
                Statut dominant
              </p>
              <p className="mt-1 truncate text-xl font-black text-slate-950 dark:text-white">
                {mainStatus.shortLabel || mainStatus.label}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5 overflow-hidden rounded-full border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-950">
          <div className="flex h-3 w-full">
            {STATUS_META.map((meta) => {
              const value = counts[meta.key] || 0;
              const width = total ? `${(value / total) * 100}%` : "0%";

              return (
                <div
                  key={meta.key}
                  className={meta.dot}
                  style={{ width }}
                  title={`${meta.label} : ${formatNumber(value)}`}
                />
              );
            })}
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {STATUS_META.map((meta) => (
            <StatusMiniCard
              key={meta.key}
              meta={meta}
              value={counts[meta.key] || 0}
              total={total}
            />
          ))}
        </div>
      </div>
    </div>
  );
}